import React from "react";
import { Box, Button, Chip, CircularProgress, IconButton, Typography } from "@mui/material";
import ArrowBack from "@mui/icons-material/ArrowBack";
import ArrowForward from "@mui/icons-material/ArrowForward";
import RefreshIcon from "@mui/icons-material/Refresh";
import { t } from "ttag";
import {
  canCompareEntity,
  compareStatusIgnoringTimestamps,
  entityHasMissingInCloud,
  entityHasMissingOnDevice,
  formatSyncTimestamp,
  resolveEntityStatus
} from "./deviceSyncCompare";
import {
  deviceDisconnectedReason,
  getHierarchySubEntities,
  getUnsupportedSyncReason,
  SyncAction,
  syncButtonDisableReason
} from "./deviceSyncConfig";
import { withButtonTooltip } from "./ButtonTooltip";
import { statusColor, StatusChip } from "./SyncStatusIndicators";
import EntityDiffTable from "./EntityDiffTable";
import useStyles from "./DeviceSync.style";

const SyncEntityPanel = ({
  entity,
  result,
  loadingCompare,
  deviceConnected,
  syncing,
  refreshing,
  onRequestSync,
  onRefresh
}: {
  entity: string;
  result: any;
  loadingCompare: boolean;
  deviceConnected: boolean;
  syncing: boolean;
  refreshing?: boolean;
  onRequestSync: (entity: string, action: SyncAction) => void;
  onRefresh?: (entity: string) => void;
}) => {
  const classes = useStyles();
  const pushBlocked = getUnsupportedSyncReason(entity, "push");
  const pullBlocked = getUnsupportedSyncReason(entity, "pull");
  const displayStatus = resolveEntityStatus(result);
  const alreadyMatches = displayStatus === "pass";
  const buttonStatus = loadingCompare ? "pending" : displayStatus;
  const pushDisabledReason = syncButtonDisableReason(entity, "push", buttonStatus, deviceConnected);
  const pullDisabledReason = syncButtonDisableReason(entity, "pull", buttonStatus, deviceConnected);
  const suggestPush = !alreadyMatches && !pushBlocked && entityHasMissingOnDevice(result);
  const suggestPull = !alreadyMatches && !pullBlocked && entityHasMissingInCloud(result);

  return (
    <Box>
      <Box className={classes.metaRow}>
        <StatusChip status={displayStatus} />
        <Typography variant="body2">
          📱 {formatSyncTimestamp(result?.deviceTimestamp)}
        </Typography>
        <Typography variant="body2">
          ☁️ {formatSyncTimestamp(result?.cloudTimestamp)}
        </Typography>
        {onRefresh && withButtonTooltip(
          !deviceConnected ? deviceDisconnectedReason() : t`Refresh this entity`,
          <IconButton
            size="small"
            disabled={!deviceConnected || !!refreshing || syncing}
            onClick={() => onRefresh(entity)}
          >
            {refreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
          </IconButton>
        )}
      </Box>
      <Box className={classes.actions}>
        {!pullBlocked && withButtonTooltip(
          pullDisabledReason
            || (suggestPull ? t`Suggestion: missing in cloud — sync device → cloud` : undefined),
          <Button
            className={suggestPull ? classes.recommendedButton : classes.directionButton}
            variant={suggestPull ? "contained" : "outlined"}
            color={suggestPull ? "primary" : "inherit"}
            disabled={!!pullDisabledReason || syncing}
            startIcon={<ArrowForward fontSize="small" />}
            onClick={() => onRequestSync(entity, "pull")}
          >
            {t`Pull`}
          </Button>
        )}
        {!pushBlocked && withButtonTooltip(
          pushDisabledReason
            || (suggestPush ? t`Suggestion: missing on device — sync cloud → device` : undefined),
          <Button
            className={suggestPush ? classes.recommendedButton : classes.directionButton}
            variant={suggestPush ? "contained" : "outlined"}
            color={suggestPush ? "primary" : "inherit"}
            disabled={!!pushDisabledReason || syncing}
            startIcon={<ArrowBack fontSize="small" />}
            onClick={() => onRequestSync(entity, "push")}
          >
            {t`Push`}
          </Button>
        )}
      </Box>
      {suggestPush && (
        <Typography className={classes.empty}>
          {t`Some items are missing on the device.`}
        </Typography>
      )}
      {suggestPull && (
        <Typography className={classes.empty}>
          {t`Some items are missing in cloud.`}
        </Typography>
      )}
      {entity === "hierarchy" ? (
        <>
          <Box sx={{ mt: 2 }}>
            <EntityDiffTable
              compare={null}
              comparable={result ? canCompareEntity(result) : undefined}
              error={result?.error}
              deviceSnapshot={result?.device}
              cloudSnapshot={result?.cloud}
              hideWhenEmpty
            />
          </Box>
          {getHierarchySubEntities().map((sub) => (
          <Box key={sub.key} sx={{ mt: 2 }}>
            <Box className={classes.metaRow}>
              <Typography variant="subtitle2">{sub.label}</Typography>
              <Chip
                size="small"
                color={statusColor(compareStatusIgnoringTimestamps(result?.subEntities?.[sub.key]))}
                label={
                  canCompareEntity(result)
                    ? compareStatusIgnoringTimestamps(result?.subEntities?.[sub.key]) || "—"
                    : "—"
                }
              />
            </Box>
            <EntityDiffTable
              compare={result?.subEntities?.[sub.key]}
              comparable={result ? canCompareEntity(result) : undefined}
              error={result?.error}
            />
          </Box>
          ))}
        </>
      ) : (
        <Box sx={{ mt: 2 }}>
          <EntityDiffTable
            compare={result?.compare}
            comparable={result ? canCompareEntity(result) : undefined}
            error={result?.error}
            deviceSnapshot={result?.device}
            cloudSnapshot={result?.cloud}
          />
        </Box>
      )}
    </Box>
  );
};

export default SyncEntityPanel;
